import {
  type CreateActivityExportProps,
  type FreezerActivityExport,
  createActivityExport,
  fetchActivityExport,
  fetchActivityExportFileUrl,
  selectFreezerActivityExportById,
  selectFreezerActivityExportDownloadUrl,
  selectFreezerCurrentActivityExport,
} from "@app/freezer";
import { useDispatch, useLoader, useSelector } from "@app/react";
import { useCallback, useEffect, useRef, useState } from "react";

export type ExportFormat = "csv" | "jsonl";

export type ExportStatus = "idle" | "creating" | "polling" | "ready" | "error";

export interface ExportFilterProps {
  organizationId: string;
  occurredAfter: string;
  occurredBefore: string;
  types?: string[];
  statuses?: string[];
  actors?: string[];
  envs?: string[];
  q?: string;
  readOnly?: boolean;
}

export interface UseActivityExportResult {
  status: ExportStatus;
  error: string;
  startExport: (format: ExportFormat) => void;
  cancel: () => void;
  exportData: FreezerActivityExport;
  downloadUrl: string;
}

const POLL_INTERVAL_MS = 3000;

export function useActivityExport(
  filters: ExportFilterProps,
): UseActivityExportResult {
  const dispatch = useDispatch();
  const [exportId, setExportId] = useState("");
  const [status, setStatus] = useState<ExportStatus>("idle");
  const [error, setError] = useState("");
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const currentExport = useSelector(selectFreezerCurrentActivityExport);

  const exportData = useSelector((s) =>
    selectFreezerActivityExportById(s, { id: exportId || "0" }),
  );

  const downloadUrl = useSelector(selectFreezerActivityExportDownloadUrl);
  const fileUrlLoader = useLoader(fetchActivityExportFileUrl);
  const createLoader = useLoader(createActivityExport);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const cancel = useCallback(() => {
    stopPolling();
    setStatus("idle");
    setExportId("");
    setError("");
  }, [stopPolling]);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const startExport = useCallback(
    (format: ExportFormat) => {
      setStatus("creating");
      setError("");
      setExportId("");

      const props: CreateActivityExportProps = {
        organizationId: filters.organizationId,
        formatType: format,
        occurredAfter: filters.occurredAfter,
        occurredBefore: filters.occurredBefore,
        types: filters.types,
        statuses: filters.statuses,
        actors: filters.actors,
        envs: filters.envs,
        q: filters.q,
        readOnly: filters.readOnly,
      };

      dispatch(createActivityExport(props));
    },
    [dispatch, filters],
  );

  useEffect(() => {
    if (status !== "creating") return;

    if (createLoader.isError) {
      setStatus("error");
      setError(createLoader.message || "Failed to create export");
      return;
    }

    if (createLoader.isSuccess && currentExport.id) {
      setExportId(currentExport.id);
      setStatus("polling");
    }
  }, [
    status,
    createLoader.isError,
    createLoader.isSuccess,
    createLoader.message,
    currentExport.id,
  ]);

  useEffect(() => {
    if (status !== "polling" || !exportId) return;

    const doPoll = () => {
      dispatch(fetchActivityExport({ id: exportId }));
    };

    doPoll();
    pollRef.current = setInterval(doPoll, POLL_INTERVAL_MS);

    return () => stopPolling();
  }, [status, exportId, dispatch, stopPolling]);

  useEffect(() => {
    if (status !== "polling" || !exportId) return;

    if (exportData.status === "succeeded") {
      stopPolling();
      dispatch(fetchActivityExportFileUrl({ id: exportId }));
    } else if (exportData.status === "failed") {
      stopPolling();
      setStatus("error");
      setError(exportData.message || "Export failed");
    }
  }, [
    status,
    exportId,
    exportData.status,
    exportData.message,
    stopPolling,
    dispatch,
  ]);

  useEffect(() => {
    if (status !== "polling" || !exportId) return;
    if (exportData.status !== "succeeded") return;

    if (fileUrlLoader.isSuccess && downloadUrl) {
      setStatus("ready");
    } else if (fileUrlLoader.isError) {
      setStatus("error");
      setError("Failed to get download URL");
    }
  }, [
    status,
    exportId,
    exportData.status,
    fileUrlLoader.isSuccess,
    fileUrlLoader.isError,
    downloadUrl,
  ]);

  return {
    status,
    error,
    startExport,
    cancel,
    exportData,
    downloadUrl: status === "ready" ? downloadUrl : "",
  };
}
